import { Box, Select as MSelect, MenuItem, SelectChangeEvent, SxProps, Typography } from '@mui/material';
import { theme } from '@src/theme';
import { buildDataTestIdAttribute, hexOpacity, scrollBarSM } from '@src/utils';
import { FC } from 'react';
import { ReactComponent as PopupIcon } from '@icons/popupIcon.svg';
import { Checkbox } from '@components/Checkbox';

type SelectProps = {
  value: string;
  clearable?: boolean;
  options: { name: string; value: string | number }[];
  placeholder?: string;
  maxWidth?: number;
  onChange?: (event: SelectChangeEvent) => void;
  multiple?: boolean;
  countOfVisibleTags?: number;
  fullWidth?: boolean;
  error?: string;
  disabled?: boolean;
  sxSelect?: SxProps;
  dataTestId?: string;
};

const Placeholder = ({ value }: { value: string }) => {
  return (
    <Typography color="grey.400" fontSize={14} fontWeight={500}>
      {value}
    </Typography>
  );
};

export const Select: FC<SelectProps> = ({
  value,
  clearable,
  options,
  placeholder = 'Select',
  maxWidth,
  onChange,
  multiple,
  countOfVisibleTags = 2,
  fullWidth = true,
  error,
  disabled,
  sxSelect,
  dataTestId,
  ...rest
}) => {
  const renderValue = (selected: string | string[] | number) => {
    if ((Array.isArray(selected) || multiple) && typeof selected !== 'number') {
      if (selected.length === 0) {
        return <Placeholder value={placeholder} />;
      }
      const selectedArr = selected as string[];
      const selectedLength = selectedArr.length;
      const countHidden =
        countOfVisibleTags && selectedLength - countOfVisibleTags > 0 ? selectedLength - countOfVisibleTags : 0;
      const countHiddenTag = countHidden ? `+ ${countHidden}` : '';
      const values = selectedArr.map(item => options.find(op => op.value === item)?.name);
      return countOfVisibleTags
        ? `${values.slice(0, countOfVisibleTags).join(', ')} ${countHiddenTag}`
        : values.join(', ');
    }
    if (`${selected}`?.trim().length === 0) {
      return <Placeholder value={placeholder} />;
    }
    return options.find(op => op.value === selected)?.name;
  };

  return (
    <Box sx={{ maxWidth: `${maxWidth}px`, width: fullWidth ? '100%' : 'none', flexGrow: 1 }}>
      <MSelect
        value={value}
        onChange={onChange}
        multiple={multiple}
        displayEmpty
        sx={{ ...sxSelect }}
        fullWidth
        error={!!error}
        disabled={disabled}
        IconComponent={PopupIcon}
        renderValue={renderValue}
        inputProps={{ ...buildDataTestIdAttribute(dataTestId) }}
        MenuProps={{
          disablePortal: true,
          sx: {
            '& .MuiList-root': {
              padding: 0,
              boxShadow: '0px 4px 8px 0px rgba(0, 0, 0, 0.10)',
              maxHeight: '200px',
              overflow: 'auto',
              ...scrollBarSM,
              '& > li': {
                fontSize: '14px',
                fontWeight: 500,
                minHeight: '40px',
                '&:hover': {
                  color: 'secondary.main',
                  backgroundColor: hexOpacity(theme.palette.secondary.main, '7%'),
                },
              },
            },
          },
        }}
        {...rest}
      >
        {options.length > 0
          ? options.map((option: { name: string; value: string | number }, index) => (
              <MenuItem
                value={option.value}
                key={option.value}
                color="secondary"
                {...buildDataTestIdAttribute(dataTestId, `row-${index}`)}
              >
                {multiple && (
                  <Checkbox
                    checked={(value as unknown as (string | number)[]).includes(option.value)}
                    color="secondary"
                  />
                )}
                {option.name}
              </MenuItem>
            ))
          : null}
      </MSelect>
    </Box>
  );
};
